import { FC, useMemo, useState } from 'react';
import Header from '@/components/UI/Header/Header';
import APITable from '@/components/UI/Table/Reusable/APITable';
import type { TanStackColumnDef } from '@/components/UI/Table/TableComponent.model';
import { RouteService } from '@/support/RouteService';
import { DateUtils } from '@/utils/Date.utils.ts';
import {
  BackfillEvent,
  BenchlingBackfillApiId,
  entityTypeToStringMap,
  errorTypeToStringMap,
  eventFilterOptions,
  statusToStringMap,
  benchlingBackfillCustomFilterProcessor,
  BACKFILL_STATUS_FILTER_CONFIG,
} from '@/model/BenchlingBackfill.model.ts';
import { useParams } from 'react-router-dom';
import Link from '@/components/UI/Link';
import { web as webRoutes } from '@/support/route.ts';
import Truncate from '@/components/UI/Truncate/Truncate.tsx';
import SubHeader from '@/components/UI/SubHeader';
import { useDispatch } from 'react-redux';
import type { UseTableProps } from '@/components/UI/Table/Reusable/APITable/APITable.model.ts';
import { successToast } from '@/helpers.tsx';
import { useApiHook } from '@/support/Hooks/api/useApiHook.ts';
import { modalAction } from '@/utils/modal.tsx';
import { RiAlertFill } from 'react-icons/ri';
import BenchlingSyncStatusAside from '@/components/UI/BenchlingSyncStatusAside/BenchlingSyncStatusAside';
import {
  BackfillEventsSyncEntityRequestProps,
  SyncEntityContext,
} from '@/components/Modals/SyncEntityDetails/SyncEntityDetails.utils';
import { _isNil, _notNil } from '@/littledash.ts';

// Type definitions for API table additional props
interface EventsMeta {
  error_count?: number;
}

interface ErrorCountAsideProps {
  useTableProps: UseTableProps<BackfillEvent>['useTableProps'];
}

const BackfillEvents: FC = () => {
  const { benchlingBackfillApiId } = useParams<{ benchlingBackfillApiId: BenchlingBackfillApiId }>();
  const dispatch = useDispatch();
  const { openModal } = modalAction(dispatch);
  const [eventsMeta, setEventsMeta] = useState<EventsMeta | null>(null);

  const closeModal = () => {
    dispatch({
      type: 'CLOSE_MODAL',
    });
  };

  const { invoke: retryEvents } = useApiHook({
    endpoint: 'POST /api/v1/backfill/{backfillApiId}/events',
    invokeOnInit: false,
  });

  const handleRetryEvents = async (
    eventApiIds: Array<BackfillEvent['api_id']>,
    refetchTableData: () => Promise<void>
  ) => {
    if (_isNil(benchlingBackfillApiId)) {
      return;
    }

    try {
      await retryEvents({
        path: { backfillApiId: benchlingBackfillApiId as BenchlingBackfillApiId },
        body: { events: eventApiIds },
      });
      await refetchTableData();
      successToast(`Retrying ${eventApiIds.length} events${eventApiIds.length > 1 ? 's' : ''}`);
    } catch (error) {
      // handled by `retryEvents`
    }
  };

  // Custom filter processor to add null exclusion for updated_at filters
  const customFilterProcessor = benchlingBackfillCustomFilterProcessor;

  // Handler for when table data is fetched to capture error count
  const handleEventsDataChange = (_data: Array<BackfillEvent>, additionalProps?: EventsMeta) => {
    setEventsMeta(additionalProps ?? null);
  };

  // Create the error count AsideComponent
  const ErrorCountAsideComponent: FC<ErrorCountAsideProps> = ({ useTableProps }) => {
    const errorCount = eventsMeta?.error_count ?? 0;

    return (
      <BenchlingSyncStatusAside
        errorCount={errorCount}
        filterConfig={BACKFILL_STATUS_FILTER_CONFIG}
        useTableProps={useTableProps}
      />
    );
  };

  const bulkActions: (props: UseTableProps<BackfillEvent>) => Array<unknown> = ({
    useTableProps: {
      selectedFlatRows,
      apiTable: { refetchTableData },
    },
  }) => {
    const eventApiIds = selectedFlatRows.map(({ original: { api_id } }) => api_id);
    const isDisabled: boolean = selectedFlatRows.some(
      ({ original }) =>
        original.status !== 'failed' && original.status !== 'cancelled' && original.status !== 'deactivated'
    );
    return [
      {
        name: `Retry sync event${selectedFlatRows?.length > 1 ? 's' : ''}`,
        disabled: isDisabled,
        tooltip: 'Only failed/cancelled events can be retried',
        action: () =>
          openModal('CONFIRM_RETRY_BACKFILL', {
            onClick: () => handleRetryEvents(eventApiIds, refetchTableData),
          }),
      },
    ];
  };

  const eventTableColumns: Array<TanStackColumnDef<BackfillEvent>> = useMemo(
    () => [
      {
        id: 'status',
        header: 'Status',
        accessorKey: 'status',
        enableSorting: true,
        isVisible: true,
        size: 60,
        cell: ({ row }: { row: { original: BackfillEvent } }) => (
          <div className="flex items-center justify-between w-100">
            {statusToStringMap[row.original.status]}
            {row.original.status === 'failed' || row.original.status === 'deactivated' ? (
              <RiAlertFill className="red" />
            ) : null}
          </div>
        ),
      },
      {
        id: 'updated_at',
        header: 'Updated at',
        accessorKey: 'updated_at',
        enableSorting: true,
        isVisible: true,
        size: 70,
        cell: ({ row }: { row: { original: BackfillEvent } }) => (
          <>{DateUtils.renderDateTime(row.original.updated_at, { defaultResponse: '-' })}</>
        ),
      },
      {
        id: 'study',
        header: 'Study',
        accessorKey: 'study',
        enableSorting: true,
        isVisible: true,
        cell: ({ row }: { row: { original: BackfillEvent } }) =>
          row.original.study.deleted ? (
            <Truncate
              tooltip={`${row.original.study.name} (deleted)`}
            >{`${row.original.study.name} (deleted)`}</Truncate>
          ) : (
            <Link
              className="link w-100"
              to={webRoutes('studies.show', {
                id: row.original.study.id,
              })}
            >
              <Truncate tooltip={row.original.study.name}>{row.original.study.name}</Truncate>
            </Link>
          ),
      },
      {
        id: 'entity_type',
        header: 'Item type',
        accessorKey: 'entity_type',
        enableSorting: true,
        isVisible: true,
        size: 70,
        cell: ({ row }: { row: { original: BackfillEvent } }) =>
          _notNil(entityTypeToStringMap[row.original.entity_type]) ? (
            <div
              className="link"
              onClick={() =>
                openModal('SYNC_ENTITY_DETAILS', {
                  syncEntityRequestProps: {
                    context: SyncEntityContext.BACKFILL_EVENTS,
                    backfillApiId: benchlingBackfillApiId as BenchlingBackfillApiId,
                    backfillEventApiId: row.original.api_id,
                  } as BackfillEventsSyncEntityRequestProps,
                  entityType: row.original.entity_type,
                })
              }
            >
              {entityTypeToStringMap[row.original.entity_type]}
            </div>
          ) : (
            '-'
          ),
      },
      {
        id: 'entity_api_id',
        header: 'Item ID',
        accessorKey: 'entity_api_id',
        enableSorting: false,
        isVisible: false,
        size: 120,
      },
      {
        id: 'error_message',
        header: 'Error message',
        enableSorting: false,
        isVisible: true,
        cell: ({ row }: { row: { original: BackfillEvent } }) => (
          <>
            {_notNil(row.original.error) ? (
              <button
                className="link-button"
                onClick={() => openModal('VIEW_EVENT_ERRORS', { error: row.original.error, closeModal })}
                data-testid="view-errors-button"
              >
                {_notNil(row.original.error_type) ? errorTypeToStringMap[row.original.error_type] : 'View'}
              </button>
            ) : (
              '-'
            )}
          </>
        ),
      },
    ],
    []
  );

  return (
    <>
      <Header mainHeaderText="Backfill Events" />
      <SubHeader linkToText="Sync history" link={webRoutes('team.studies.benchling_sync.tab', { tab: 'backfills' })} />
      <div className="mv3">
        <APITable
          apiInfo={{
            type: 'internalApi',
            route: RouteService.api({
              endpoint: 'GET /api/v1/backfill/{backfillApiId}/events',
              path: { backfillApiId: benchlingBackfillApiId as BenchlingBackfillApiId },
            }).url,
          }}
          columns={eventTableColumns}
          bulkActions={bulkActions}
          filterOptions={eventFilterOptions}
          customFilterProcessor={customFilterProcessor}
          defaultSortBy={{ id: 'updated_at', desc: true }}
          reduxTableName="benchlingBackfillEvents"
          hideSearch
          testIdPrefix="backfill-events"
          onFetchTableData={handleEventsDataChange}
          AsideComponent={ErrorCountAsideComponent}
        />
      </div>
    </>
  );
};

export default BackfillEvents;
